import store from '@/store';
import H_time from '@/helpers/time';
import H_relatedModuleDeleteInfo from '@/helpers/relatedModuleDeleteInfo';
import Spare_factorySystemClass from '@/spare/factorySystemClass';

const DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';

export default {
  setIndex(rawDataList) {
    return rawDataList.map(({
      id,
      name,
      is_disabled,
      factory_system_classes,
      updated_user,
      updated_at,
    }) => ({
      id,
      systemClass: this.setTextChip({name, is_disabled}),
      systemSubclasses: this.setSystemSubclasses(factory_system_classes),
      updatedUser: updated_user,
      updatedAt: H_time.getDateDashFormat(updated_at, DATETIME_FORMAT),
      isDisabled: !!is_disabled,
    }));
  },
  setShow({
    id,
    name,
    updated_user,
    updated_at,
    is_disabled,
  }) {
    return {
      id,
      name,
      updatedUser: updated_user || {
        name: '',
        avatar: '',
      },
      updatedAt: H_time.getDateDashFormat(updated_at, DATETIME_FORMAT),
      isDisabled: !!is_disabled,
    };
  },
  setTextChip({name, is_disabled}) {
    return {
      text: name,
      chips: is_disabled ? [{
        name: Spare_factorySystemClass.disabledLabel,
        color: 'red',
      }] : [],
    };
  },
  setSystemSubclasses(factory_system_classes) {
    return (factory_system_classes || []).map(subclass => ({
      ...subclass,
      isDisabled: !!subclass.is_disabled,
      color: subclass.is_disabled ? 'red' : subclass.color,
    }));
  },
  setSubmitCreateData(rawData, totalDataNum) {
    return {
      name: rawData.name,
      sequence: (totalDataNum + 1).toString().padStart(4, '0'),
    };
  },
  setSubmitUpdateData(rawData) {
    return {
      name: rawData.name,
    };
  },
  setSubmitSubclassSortData(parentId, sortList) {
    const factory_system_classes = sortList.map(({ id }) => ({ id }));
    return {
      factory_system_classes: parentId
        ? [{ id: parentId, factory_system_classes }]
        : factory_system_classes,
    };
  },
  setDeleteInfo(rawData) {
    return H_relatedModuleDeleteInfo.setDeleteInfo(
      rawData,
      Spare_factorySystemClass.deleteInUseContentText,
    );
  },
  setSubmitClassSortData(rawSortList) {
    const sortList = rawSortList.map(({id, items}) => ({
      id,
      factory_system_classes: items.map(({id: itemId}) => ({id: itemId})),
    }));
    return {
      factory_system_classes: sortList,
    };
  },
  setSubmitModuleData(selectedSystemClasses) {
    const roots = selectedSystemClasses || [];
    const systemClassRoot = roots.find(node => node.id === 'systemClass');
    const factorySystemClassRoot = roots.find(node => node.id === 'factorySystemClass');

    const system_classes = [];
    const system_subclasses = [];
    for (const row of systemClassRoot?.items || []) {
      system_classes.push(row.id);
      for (const leaf of row.items || []) {
        system_subclasses.push(leaf.id);
      }
    }

    const factory_system_classes = [];
    for (const row of factorySystemClassRoot?.items || []) {
      factory_system_classes.push(row.id);
      for (const leaf of row.items || []) {
        factory_system_classes.push(leaf.id);
      }
    }

    const uniqIds = ids => [...new Set(ids)];
    return {
      system_classes: uniqIds(system_classes),
      system_subclasses: uniqIds(system_subclasses),
      factory_system_classes: uniqIds(factory_system_classes),
    };
  },
  setSubmitDataFromSelectValue(selectValue) {
    const pickIds = list => (list || []).map(item => item?.id ?? item);
    const factory_system_classes = [
      ...pickIds(selectValue?.factory_system_classes),
      ...pickIds(selectValue?.factory_system_subclasses),
    ];
    const uniqIds = ids => [...new Set(ids)];
    return {
      system_classes: uniqIds(pickIds(selectValue?.system_classes)),
      system_subclasses: uniqIds(pickIds(selectValue?.system_subclasses)),
      factory_system_classes: uniqIds(factory_system_classes),
    };
  },
  getExamSubclassIdsFromMultiSystemClasses(selectedSystemClasses) {
    const roots = selectedSystemClasses || [];
    if (!roots.length) {
      return { system_subclasses: [], factory_system_subclasses: [] };
    }

    const pickLeafIds = rootId => {
      const root = roots.find(node => node.id === rootId);
      if (!root) return [];
      const ids = [];
      for (const row of root.items || []) {
        for (const leaf of row.items || []) ids.push(leaf.id);
      }
      return [...new Set(ids)];
    };

    return {
      system_subclasses: pickLeafIds('systemClass'),
      factory_system_subclasses: pickLeafIds('factorySystemClass'),
    };
  },
  isExamIdsInAllowedList(examSubclassIdList, allSubclassIdList) {
    const examIds = examSubclassIdList || [];
    if (!examIds.length) return true;
    const allowedSet = new Set((allSubclassIdList || []).map(id => String(id)));
    return examIds.every(id => allowedSet.has(String(id)));
  },
  hasSystemSubclassScopeByList(examSubclassIdList, allSystemSubclassIdList, factoryId) {
    const currentUser = store?.state?.stone_auth?.currentUser;
    if (currentUser?.is_administrator) return true;

    let allowed = allSystemSubclassIdList;
    if (allowed == null) {
      const currentFactoryId = factoryId ?? store.state.app.factoryId;
      const scope = currentUser?.user_factory_system_subclasses?.find(
        item => item.factory.id === currentFactoryId,
      );
      if (!scope) return false;
      allowed = (scope.system_subclasses || []).map(item => item.id);
    }
    return this.isExamIdsInAllowedList(examSubclassIdList, allowed);
  },
  hasFactorySystemSubclassScopeByList(examSubclassIdList, allFactorySystemSubclassIdList, factoryId) {
    const currentUser = store?.state?.stone_auth?.currentUser;
    if (currentUser?.is_administrator) return true;

    let allowed = allFactorySystemSubclassIdList;
    if (allowed == null) {
      const currentFactoryId = factoryId ?? store.state.app.factoryId;
      const scope = currentUser?.user_factory_system_subclasses?.find(
        item => item.factory.id === currentFactoryId,
      );
      if (!scope) return false;
      allowed = (scope.factory_system_subclasses || []).map(item => item.id);
    }
    return this.isExamIdsInAllowedList(examSubclassIdList, allowed);
  },
  hasSystemAndFactorySubclassScopeByList(
    examSystemSubclassIdList,
    examFactorySystemSubclassIdList,
    factoryId,
  ) {
    const systemExamIds = examSystemSubclassIdList || [];
    const factoryExamIds = examFactorySystemSubclassIdList || [];
    if (!systemExamIds.length && !factoryExamIds.length) return true;

    const currentUser = store?.state?.stone_auth?.currentUser;
    if (currentUser?.is_administrator) return true;

    const currentFactoryId = factoryId ?? store.state.app.factoryId;
    const scope = currentUser?.user_factory_system_subclasses?.find(
      item => item.factory.id === currentFactoryId,
    );
    if (!scope) return false;

    const systemAllowed = (scope.system_subclasses || []).map(item => item.id);
    const factoryAllowed = (scope.factory_system_subclasses || []).map(item => item.id);
    return (
      this.isExamIdsInAllowedList(systemExamIds, systemAllowed) &&
      this.isExamIdsInAllowedList(factoryExamIds, factoryAllowed)
    );
  },
  hasSystemSubclassScope(systemSubclassId, factoryId) {
    if (systemSubclassId == null) return false;
    return (
      this.hasSystemSubclassScopeByList([systemSubclassId], null, factoryId) ||
      this.hasFactorySystemSubclassScopeByList([systemSubclassId], null, factoryId)
    );
  },
  getEmptyMultiSystemClassesSelectValue() {
    return {
      system_classes: [],
      system_subclasses: [],
      factory_system_classes: [],
      factory_system_subclasses: [],
    };
  },
  getTemplateMultiSystemClassesSelectValue(systemSubclasses) {
    return {
      system_classes: [],
      system_subclasses: systemSubclasses || [],
      factory_system_classes: [],
      factory_system_subclasses: [],
    };
  },
  getMultiSystemClassesSelectValueFromApi(rawData) {
    const data = rawData || {};
    return {
      system_classes: data.system_classes || [],
      system_subclasses: data.system_subclasses || [],
      factory_system_classes: data.factory_system_classes || [],
      factory_system_subclasses: data.factory_system_subclasses || [],
    };
  },
  getTopNodeDisableListForTemplate(templateId) {
    return !templateId || templateId === 'other' ? [] : ['systemClass'];
  },
};
